import type { StockPurchase, StockPurchaseStatus } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ChipFilter,
  Container,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  NoPermissionState,
  PageHeader,
  RowActions,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { PackageCheck, Plus, ShoppingCart } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { ReasonDialog } from '@/features/pos/ReasonDialog';
import { quantityLabel } from '@/features/stock/labels';
import { PurchaseDrawer } from '@/features/stock/PurchaseDrawer';
import {
  cancelStockPurchase,
  receiveStockPurchase,
  stockItemsQueryOptions,
  stockKeys,
  stockLocationsQueryOptions,
  stockPurchasesQueryOptions,
} from '@/features/stock/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

export const Route = createFileRoute('/_app/stock/purchases')({ component: StockPurchasesPage });

const STATUSES: StockPurchaseStatus[] = ['DRAFT', 'RECEIVED', 'CANCELLED'];
const STATUS_TONE: Record<StockPurchaseStatus, 'amber' | 'green' | 'neutral'> = {
  DRAFT: 'amber',
  RECEIVED: 'green',
  CANCELLED: 'neutral',
};

/** Bons d'achat et réceptions (ADR-020, D-H32). */
function StockPurchasesPage() {
  const { t, i18n } = useTranslation(['stock', 'common', 'errors']);
  const session = useSession();
  const queryClient = useQueryClient();
  const { propertyId } = useCurrentProperty();
  const canRead = session.can('stock.read');
  const canPurchase = session.can('stock.purchase');
  const [status, setStatus] = useState<StockPurchaseStatus | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [receiving, setReceiving] = useState<StockPurchase | null>(null);
  const [cancelling, setCancelling] = useState<StockPurchase | null>(null);
  const query = useInfiniteQuery(stockPurchasesQueryOptions({ propertyId, status }, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const items = useQuery(stockItemsQueryOptions({ propertyId }, canPurchase && drawerOpen));
  const locations = useQuery(stockLocationsQueryOptions(propertyId, canPurchase && drawerOpen));

  const receive = useMutation({
    mutationFn: (purchase: StockPurchase) =>
      receiveStockPurchase(purchase.id, { version: purchase.version }),
    onSuccess: async () => {
      toast.success(t('stock:purchases.toasts.received'));
      await queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setReceiving(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const cancel = useMutation({
    mutationFn: ({ purchase, reason }: { purchase: StockPurchase; reason: string }) =>
      cancelStockPurchase(purchase.id, { version: purchase.version, reason }),
    onSuccess: async () => {
      toast.success(t('stock:purchases.toasts.cancelled'));
      await queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setCancelling(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('stock:purchases.title')}
        subtitle={t('stock:purchases.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('stock:title'), link: <Link to="/stock">{t('stock:title')}</Link> },
          { label: t('stock:purchases.title') },
        ]}
        actions={
          canPurchase && propertyId ? (
            <Button variant="primary" onClick={() => setDrawerOpen(true)}>
              <Plus className="ico" aria-hidden="true" />
              {t('stock:purchases.new')}
            </Button>
          ) : null
        }
      />
      <Card>
        <CardBody density="flush">
          <Toolbar>
            <div className="row row--wrap" style={{ gap: 6 }}>
              <ChipFilter active={status === undefined} onClick={() => setStatus(undefined)}>
                {t('stock:purchases.allStatuses')}
              </ChipFilter>
              {STATUSES.map((value) => (
                <ChipFilter key={value} active={status === value} onClick={() => setStatus(value)}>
                  {t(`stock:purchases.status.${value}`)}
                </ChipFilter>
              ))}
            </div>
          </Toolbar>
          {query.isPending ? (
            <TableSkeleton rows={5} columns={7} label={t('common:states.loading')} />
          ) : query.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ShoppingCart className="ico" aria-hidden="true" />}
              title={t('stock:purchases.empty')}
              description={t('stock:purchases.emptyHint')}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('stock:purchases.columns.reference')}</Th>
                    <Th>{t('stock:purchases.columns.supplier')}</Th>
                    <Th>{t('stock:purchases.columns.location')}</Th>
                    <Th>{t('stock:purchases.columns.date')}</Th>
                    <Th>{t('stock:purchases.columns.lines')}</Th>
                    <Th align="right">{t('stock:purchases.columns.total')}</Th>
                    <Th>{t('stock:purchases.columns.status')}</Th>
                    {canPurchase ? <Th align="right">{t('common:actions.actions')}</Th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((purchase) => (
                    <Tr key={purchase.id}>
                      <Td className="mono">{purchase.reference}</Td>
                      <Td>
                        <div className="stack" style={{ gap: 2 }}>
                          <strong>{purchase.supplierName}</strong>
                          {purchase.supplierReference ? (
                            <span className="t-12 muted">{purchase.supplierReference}</span>
                          ) : null}
                        </div>
                      </Td>
                      <Td>{purchase.locationName}</Td>
                      <Td>{formatDate(purchase.orderedAt, i18n.language)}</Td>
                      <Td>
                        <div className="stack" style={{ gap: 2 }}>
                          {purchase.lines.slice(0, 3).map((line) => (
                            <span key={line.id} className="t-12">
                              {quantityLabel(line.packageQuantity, i18n.language)} × {line.itemName}
                              {line.packageLabel ? ` (${line.packageLabel})` : ''}
                            </span>
                          ))}
                          {purchase.lines.length > 3 ? (
                            <span className="t-12 muted">
                              {t('stock:purchases.lineSummary', { count: purchase.lines.length })}
                            </span>
                          ) : null}
                        </div>
                      </Td>
                      <Td numeric align="right">
                        {formatAmount(purchase.totalCost, i18n.language, purchase.currencyCode)}
                      </Td>
                      <Td>
                        <Badge tone={STATUS_TONE[purchase.status]}>
                          {t(`stock:purchases.status.${purchase.status}`)}
                        </Badge>
                      </Td>
                      {canPurchase ? (
                        <Td align="right">
                          {purchase.status === 'DRAFT' ? (
                            <RowActions>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => setReceiving(purchase)}
                              >
                                <PackageCheck className="ico ico-16" aria-hidden="true" />
                                {t('stock:purchases.receive')}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setCancelling(purchase)}
                              >
                                {t('stock:purchases.cancel')}
                              </Button>
                            </RowActions>
                          ) : null}
                        </Td>
                      ) : null}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>

      {canPurchase && propertyId ? (
        <PurchaseDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          propertyId={propertyId}
          items={items.data ?? []}
          locations={locations.data ?? []}
          currencyCode={session.user.tenant.currencyCode}
        />
      ) : null}

      <Dialog open={receiving !== null} onOpenChange={(open) => !open && setReceiving(null)}>
        <DialogContent
          title={t('stock:purchases.receive')}
          description={
            receiving
              ? `${receiving.reference} — ${receiving.supplierName} → ${receiving.locationName}`
              : undefined
          }
          icon={<PackageCheck className="ico" aria-hidden="true" />}
          iconTone="green"
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="secondary" type="button" onClick={() => setReceiving(null)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                type="button"
                loading={receive.isPending}
                onClick={() => receiving && receive.mutate(receiving)}
              >
                {t('common:actions.confirm')}
              </Button>
            </>
          }
        >
          {receiving ? (
            <ul className="stack" style={{ gap: 4, margin: 0, paddingLeft: 18 }}>
              {receiving.lines.map((line) => (
                <li key={line.id}>
                  {quantityLabel(line.unitQuantity, i18n.language)} {t(`stock:unit.${line.unit}`)} —{' '}
                  {line.itemName}
                </li>
              ))}
            </ul>
          ) : null}
        </DialogContent>
      </Dialog>

      <ReasonDialog
        open={cancelling !== null}
        onOpenChange={(open) => !open && setCancelling(null)}
        title={t('stock:purchases.cancelTitle')}
        description={t('stock:purchases.cancelDescription')}
        confirmLabel={t('stock:purchases.cancel')}
        pending={cancel.isPending}
        onConfirm={(reason) => cancelling && cancel.mutate({ purchase: cancelling, reason })}
      />
    </Container>
  );
}
